import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useLang } from '../../context/LangContext';
import { useUserAuth } from '../../context/UserAuthContext';
import { useAuctionSocket } from '../../hooks/useAuctionSocket';
import { BiddingRange } from '../../types/socket.types';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ResponsiveView from '../../layout/ResponsiveView';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import { FormTitle } from '../../customComponents/FormTitle';
import { CustomDropdown } from '../../customComponents/CustomDropdown';
import AgreementButton from '../../components/AgreementButton';
import TermsConsentSheet from '../../components/TermsConsentSheet';
import AnimatedBorderButton, { AnimatedBorderButtonRef } from '../../components/AnimatedBorderButton';
import BidHistoryUpdateModal from '../../components/BidHistoryUpdateModal';
import LotEndedModal from '../../components/LotEndedModal';
import BidRestrictedModal from '../../components/BidRestrictedModal';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: 140px; /* 고정 버튼 영역을 위한 하단 패딩 */
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px 0;
  gap: 24px;
`;

// 정보 섹션
const InfoSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 32px 0;
  margin-bottom: 8px;
  border-top: 1px solid #f3f3f3;
  border-bottom: 1px solid #f3f3f3;
`;

const LotImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
  background: #f0f0f0;
`;

const LotInfoContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  justify-content: flex-start;
`;

const LotName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #000;
  line-height: 1.4;
  text-align: left;
  align-self: flex-start;
`;

const LotId = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: 500;
  text-align: left;
  align-self: flex-start;
`;

// 금액 섹션
const AmountSection = styled.div`
  background: white;
  border-radius: 12px;
  padding-top: 20px;
  margin-bottom: 8px;
`;

const DropdownContainer = styled.div`
  margin-bottom: 20px;
`;

const FeeCalculationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const FeeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #262626;
`;

const FeeDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e9ecef;
  margin: 12px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #7a6647;
  padding-top: 8px;
`;

// 드롭다운 옵션 스타일 컴포넌트들
const DropdownOptionContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const CurrencyText = styled.span`
  color: #b6a488;
  font-size: 12px;
  font-weight: 500;
`;

const AmountText = styled.span`
  color: #7a6647;
  font-size: 16px;
  font-weight: 600;
`;

// 입찰 유의사항 스타일
const BiddingNoticeContainer = styled.div`
  background: #f8f5f0;
  border-radius: 8px;
  padding: 16px;
  min-height: 137px; /* 최소 높이로 변경 */
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const BiddingNoticeTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin: 0;
`;

const InfoIcon = styled.svg`
  width: 20px;
  height: 20px;
  fill: #666666;
  flex-shrink: 0;
`;

const BiddingNoticeList = styled.ul`
  margin: 0;
  padding: 0 0 0 12px; /* 왼쪽에 12px 여백 추가 */
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BiddingNoticeItem = styled.li`
  font-size: 14px;
  color: #666666;
  line-height: 1.4;
  position: relative;
  padding-left: 16px; /* 점과 텍스트 사이 간격 줄임 */
  text-align: left; /* 명시적으로 왼쪽 정렬 */
  
  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #999999;
  }
`;

// 동의 섹션 스타일
const AgreementSectionWrapper = styled.div`
  background: white;
  border-radius: 12px;
  margin-bottom: 8px;
`;

const AgreementItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const ViewButton = styled.button`
  background: none;
  border: none;
  color: #666666;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    color: #333333;
  }
`;

// 고정 버튼 컨테이너
const FixedButtonContainer = styled.div`
  position: fixed;
  bottom: 75px; /* 앱에서 하단 네비게이션 위로 */
  left: 0;
  right: 0;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  z-index: 999;
  border-top: 1px solid #e9ecef;

  @media (min-width: 768px) {
    bottom: 0; /* 웹에서는 항상 바닥에 고정 */
    padding: 20px 24px;
  }
`;

const ButtonWrapper = styled.div`
  width: 100%;
  

  @media (min-width: 768px) {
    max-width: 1200px;
  }
`;

// 툴팁 관련 스타일
const TooltipContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const QuestionIcon = styled.div.attrs<{}, { ref?: React.Ref<HTMLDivElement> }>(() => ({}))`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #666666;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #333333;
  }
`;

const Tooltip = styled.div<{ 
  visible: boolean; 
  $left: string; 
  $transform: string; 
  $arrowLeft: string; 
}>`
  position: absolute;
  bottom: 100%;
  left: ${props => props.$left};
  transform: ${props => props.$transform};
  background-color: #333333;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: opacity 0.2s, visibility 0.2s;
  margin-bottom: 4px;
  min-width: 280px; /* 최소 너비 설정 */
  text-align: center;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: ${props => props.$arrowLeft};
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: #333333;
  }
`;

const bidRequestTexts = {
  ko: {
    title: 'LOT 입찰 신청',
    infoSection: '경매 정보',
    amountSection: '입찰가',
    bidAmountLabel: '입찰가',
    bidAmountPlaceholder: '입찰가를 선택해주세요',
    auctionHouseFee: '경매장 수수료',
    onlineAuctionFee: '온라인경매 수수료',
    feeVAT: '수수료 부가세',
    feeVATTooltip: '경매장 수수료와 온라인경매 수수료의 합산에 대한 부가세입니다',
    totalAmount: '총 합계',
    currency: 'KRW',
    agreementSection: '동의사항',
    allAgreeTitle: '전체 동의',
    auctionTermsTitle: '경매장 이용약관 동의',
    auctionTermsDetailTitle: '경매장 이용약관 상세',
    agreeButton: '동의',
    bidButton: '꾹 눌러 입찰 신청',
    bidButtonTooltip: '1초간 꾹 눌러서 입찰',
    biddingNoticeTitle: '입찰 시 유의사항',
    biddingNoticeItem1: '라이브 경매 중에는 금액 수정이 불가합니다.',
    biddingNoticeItem2: '직접·메일 입찰 금액 동일 시, 시간 순 우선 처리됩니다.',
    biddingNoticeItem3: '경매 수수료는 경매 별로 상이합니다.'
  },
  en: {
    title: 'LOT Bid Request',
    infoSection: 'Auction Information',
    amountSection: 'Bid Price',
    bidAmountLabel: 'Bid Price',
    bidAmountPlaceholder: 'Please select bid price',
    auctionHouseFee: 'Auction House Fee',
    onlineAuctionFee: 'Online Auction Fee',
    feeVAT: 'Fee VAT',
    feeVATTooltip: 'VAT on the combined auction house and online auction fees',
    totalAmount: 'Total Amount',
    currency: 'USD',
    agreementSection: 'Agreements',
    allAgreeTitle: 'Agree All',
    auctionTermsTitle: 'Agree to Auction Terms',
    auctionTermsDetailTitle: 'Auction Terms Details',
    agreeButton: 'Agree',
    bidButton: 'Hold to Submit Bid',
    bidButtonTooltip: 'Hold for 1 second to bid',
    biddingNoticeTitle: 'Bidding Precautions',
    biddingNoticeItem1: 'Amount modification is not possible during live auction.',
    biddingNoticeItem2: 'When direct and mail bid amounts are the same, they are processed in time order.',
    biddingNoticeItem3: 'Auction fees vary by auction.'
  }
};

function LiveBidRequest() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLang();
  const texts = bidRequestTexts[lang];
  const { isLoggedIn, user } = useUserAuth();
  
  // 토큰 만료 시 로그인 페이지로 리다이렉트
  useEffect(() => {
    if (!isLoggedIn) {
      devLog('🔒 [LiveBidRequest] 토큰 만료 또는 미로그인 상태 - 로그인 페이지로 리다이렉트');
      const loginMessage = lang === 'ko' ? '로그인이 필요합니다.' : 'Login required.';
      toast.error(loginMessage);
      navigate('/login', { replace: true });
      return;
    }
  }, [isLoggedIn, navigate, lang]);
  
  // location.state로부터 파라미터 추출 (LiveBid와 동일한 로직)
  const { auctionIndex: paramAuctionIndex, lotIndex: paramLotIndex, selectedItem } = location.state || {};

  // selectedItem 구조 분석
  const auctionIndex = paramAuctionIndex || 
                      selectedItem?.apiData?.auction?.index || 
                      selectedItem?.apiData?.auction?.id ||
                      selectedItem?.auctionIndex || 
                      selectedItem?.auctionId;
                      
  const lotIndex = paramLotIndex || 
                  selectedItem?.apiData?.index || 
                  selectedItem?.id ||
                  selectedItem?.lotIndex || 
                  selectedItem?.index;

  devLog('🎯 [LiveBidRequest] 파라미터 분석 상세:', {
    location: location.state,
    paramAuctionIndex,
    paramLotIndex,
    selectedItem,
    auctionIndex,
    lotIndex,
    auctionIndexType: typeof auctionIndex,
    auctionIndexValid: auctionIndex !== undefined && auctionIndex !== null && !isNaN(Number(auctionIndex)),
    lotIndexType: typeof lotIndex,
    lotIndexValid: lotIndex !== undefined && lotIndex !== null && !isNaN(Number(lotIndex))
  });
  
  // 소켓 데이터 가져오기 - 자동 데이터 요청 비활성화하여 중복 호출 방지
  const {
    isConnected,
    auctionData,
    bidHistory,
    isBanned, // 사용자 차단 상태
    error,
    getAuctionInfo,
    getBidHistory,
    placeBid,
    clearError
  } = useAuctionSocket({
    auctionIndex,
    autoConnect: true,
    autoRequestData: false // 자동 데이터 요청 비활성화 - 컴포넌트에서 직접 제어
  });

  // getAuctionInfo, getBidHistory 함수를 ref로 저장하여 최신 함수 참조 유지
  const getAuctionInfoRef = useRef(getAuctionInfo);
  getAuctionInfoRef.current = getAuctionInfo;
  
  const getBidHistoryRef = useRef(getBidHistory);
  getBidHistoryRef.current = getBidHistory;

  // React Strict Mode 중복 호출 감지 (개발 모드에서만)
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  
  if (process.env.NODE_ENV === 'development' && renderCountRef.current > 2) {
    devWarn('🔄 [LiveBidRequest] 과도한 리렌더링 감지:', {
      renderCount: renderCountRef.current,
      reason: 'React Strict Mode나 의존성 배열 문제일 수 있음'
    });
  }

  devLog('🎯 [LiveBidRequest] useAuctionSocket 초기화:', {
    auctionIndex,
    autoConnect: true,
    isConnected,
    auctionData,
    error,
    renderCount: renderCountRef.current
  });

  // 소켓 데이터 변화 감지
  useEffect(() => {
    devLog('🎯 [LiveBidRequest] 소켓 데이터 변화:', { 
      isConnected, 
      auctionData: auctionData ? 'available' : 'null',
      error 
    });
  }, [isConnected, auctionData, error]);

  // 소켓 연결 후 경매 정보 요청 - 중복 호출 방지
  const hasRequestedAuctionInfo = useRef(false);
  const lastRequestedAuctionIndex = useRef<number | null>(null);
  
  useEffect(() => {
    devLog('🎯 [LiveBidRequest] useEffect 실행:', { isConnected, auctionIndex });
    
    if (isConnected && auctionIndex) {
      // 이미 같은 auctionIndex로 요청했으면 스킵
      if (hasRequestedAuctionInfo.current && lastRequestedAuctionIndex.current === auctionIndex) {
        devLog('🎯 [LiveBidRequest] 경매 정보 요청 스킵 (이미 요청함):', {
          auctionIndex,
          lastRequestedAuctionIndex: lastRequestedAuctionIndex.current
        });
        return;
      }
      
      devLog('🎯 [LiveBidRequest] 경매 정보 요청 (최초 또는 auctionIndex 변경):', {
        auctionIndex,
        lastRequestedAuctionIndex: lastRequestedAuctionIndex.current
      });
      
      hasRequestedAuctionInfo.current = true;
      lastRequestedAuctionIndex.current = auctionIndex;
      getAuctionInfoRef.current(auctionIndex);
    }
    
    // 연결이 끊어지면 플래그 리셋
    if (!isConnected) {
      hasRequestedAuctionInfo.current = false;
      lastRequestedAuctionIndex.current = null;
    }
  }, [isConnected, auctionIndex]); // getAuctionInfo 의존성 제거

  // 입찰 내역은 소켓 이벤트로만 받음 - 중복 API 호출 방지
  // LiveBid에서 이미 소켓을 통해 bid-history 이벤트를 리스닝하고 있으므로
  // 추가적인 getBidHistory API 호출 없이 실시간 이벤트 데이터만 활용
  // useAuctionSocket의 handleBidHistory와 handleBidPlaced에서 자동으로 처리됨
  
  // 소켓 연결 후 경매 정보 및 입찰 내역 요청 - 중복 호출 방지
  const hasRequestedBidHistory = useRef(false);
  const lastRequestedLotIndex = useRef<number | null>(null);
  
  useEffect(() => {
    if (auctionData && auctionData.lots && auctionData.lots.length > 0 && lotIndex && isConnected) {
      devLog('🎯 [LiveBidRequest] 경매 데이터 로드됨 - 특정 lot의 입찰 내역 요청:', {
        auctionIndex,
        lotIndex,
        lotsCount: auctionData.lots.length,
        bidHistoryLength: bidHistory.length
      });

      // 이미 같은 lotIndex로 요청했으면 스킵
      if (hasRequestedBidHistory.current && lastRequestedLotIndex.current === lotIndex) {
        devLog('🎯 [LiveBidRequest] 입찰 내역 요청 스킵 (이미 요청함):', {
          lotIndex,
          lastRequestedLotIndex: lastRequestedLotIndex.current
        });
        return;
      }
      
      devLog('🎯 [LiveBidRequest] 입찰 내역 요청 (최초 또는 lotIndex 변경):', {
        auctionIndex,
        lotIndex,
        lastRequestedLotIndex: lastRequestedLotIndex.current
      });
      
      hasRequestedBidHistory.current = true;
      lastRequestedLotIndex.current = lotIndex;
      getBidHistoryRef.current(auctionIndex, lotIndex);
    }
    
    // 연결이 끊어지거나 경매 데이터가 없으면 플래그 리셋
    if (!isConnected || !auctionData) {
      hasRequestedBidHistory.current = false;
      lastRequestedLotIndex.current = null;
    }
  }, [auctionData, auctionIndex, lotIndex, bidHistory.length, isConnected]);

  // auctionIndex가 없으면 경고 표시
  useEffect(() => {
    if (!auctionIndex) {
      devWarn('🎯 [LiveBidRequest] auctionIndex가 없습니다:', { 
        location: location.state,
        paramAuctionIndex,
        selectedItem
      });
      const auctionNotFoundMessage = lang === 'ko' ? '경매 정보를 찾을 수 없습니다.' : 'Auction information not found.';
      toast.error(auctionNotFoundMessage);
      return;
    }
    
    // auctionIndex가 유효한 숫자인지 확인
    if (isNaN(Number(auctionIndex))) {
      devError('🎯 [LiveBidRequest] auctionIndex가 유효한 숫자가 아닙니다:', {
        auctionIndex,
        type: typeof auctionIndex,
        converted: Number(auctionIndex)
      });
      const invalidAuctionMessage = lang === 'ko' ? '잘못된 경매 정보입니다.' : 'Invalid auction information.';
      toast.error(invalidAuctionMessage);
      return;
    }
    
    devLog('🎯 [LiveBidRequest] 유효한 auctionIndex 확인됨:', {
      auctionIndex,
      type: typeof auctionIndex,
      asNumber: Number(auctionIndex)
    });
  }, [auctionIndex, location.state, paramAuctionIndex, selectedItem, lang]);

  // 에러 처리
  useEffect(() => {
    if (error) {
      devError('🎯 [LiveBidRequest] 소켓 에러:', error);
      
      // 서버 내부 에러는 토스트로 간단히 표시하고 자동으로 클리어
      const isServerInternalError = error.includes('Buffer') || 
                                   error.includes('ArrayBuffer') || 
                                   error.includes('undefined') ||
                                   error.includes('서버에서 일시적인 오류');
      
      if (isServerInternalError) {
        devLog('🔧 [LiveBidRequest] 서버 내부 에러 감지 - 토스트 표시 후 자동 클리어');
        toast.warn('서버 연결 중 일시적인 오류가 발생했습니다.');
        
        // 3초 후 자동으로 에러 클리어
        setTimeout(() => {
          clearError();
        }, 3000);
      } else {
        // 다른 에러는 기존 방식대로 처리
        toast.error(`소켓 연결 오류: ${error}`);
        
        // 10초 후 자동으로 에러 클리어
        setTimeout(() => {
          clearError();
        }, 10000);
      }
    }
  }, [error, clearError]);

  // live-bid-request 전용 인증 에러 핸들러
  useEffect(() => {
    const handleLiveBidRequestAuthError = (event: CustomEvent) => {
      devError('🔒 [LiveBidRequest] 인증 에러 감지:', event.detail);
      
      // 토스트 메시지 표시
      const expiredMessage = lang === 'ko' ? '로그인이 만료되었습니다.' : 'Login has expired.';
      toast.error(expiredMessage);
      
      // 로그인 페이지로 리다이렉트
      navigate('/login', { replace: true });
    };

    // live-bid-request 전용 인증 에러 이벤트 리스너 등록
    window.addEventListener('live-bid-request-auth-error', handleLiveBidRequestAuthError as EventListener);
    
    return () => {
      window.removeEventListener('live-bid-request-auth-error', handleLiveBidRequestAuthError as EventListener);
    };
  }, [navigate, lang]);

  
  // 상태 관리
  const [selectedBidPrice, setSelectedBidPrice] = useState('');
  const [isIndividualAgreed, setIsIndividualAgreed] = useState(false);
  const [showTermsSheet, setShowTermsSheet] = useState(false);
  const [viewedSection, setViewedSection] = useState('auction-terms');
  const [showVATTooltip, setShowVATTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ left: '50%', transform: 'translateX(-50%)', arrowLeft: '50%' });
  const questionIconRef = useRef<HTMLDivElement>(null);

  // 툴팁 위치 계산 함수
  const calculateTooltipPosition = useCallback(() => {
    if (!questionIconRef.current) return;
    
    const iconRect = questionIconRef.current.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const tooltipWidth = 300; // 예상 툴팁 너비
    
    // 아이콘이 화면 왼쪽에서 가까운 경우
    if (iconRect.left < tooltipWidth / 2) {
      setTooltipPosition({
        left: '0px',
        transform: 'none',
        arrowLeft: `${iconRect.width / 2}px`
      });
    }
    // 아이콘이 화면 오른쪽에서 가까운 경우
    else if (iconRect.right > windowWidth - tooltipWidth / 2) {
      setTooltipPosition({
        left: 'auto',
        transform: 'translateX(-100%)',
        arrowLeft: `${tooltipWidth - iconRect.width / 2}px`
      });
    }
    // 일반적인 경우 (중앙 정렬)
    else {
      setTooltipPosition({
        left: '50%',
        transform: 'translateX(-50%)',
        arrowLeft: '50%'
      });
    }
  }, []);
  const [isSubmittingBid, setIsSubmittingBid] = useState(false); // 입찰 진행 상태
  const [bidSubmitTime, setBidSubmitTime] = useState<number | null>(null); // 입찰 요청 시간
  const [lastBidRequestId, setLastBidRequestId] = useState<string | null>(null); // 마지막 입찰 요청 ID (중복 방지)
  const [showBidUpdateModal, setShowBidUpdateModal] = useState(false); // 입찰 갱신 모달
  const [showLotEndedModal, setShowLotEndedModal] = useState(false); // lot 종료 모달
  const [showBidRestrictedModal, setShowBidRestrictedModal] = useState(false); // 입찰 제한 모달
  const [bidHistorySnapshot, setBidHistorySnapshot] = useState<any[]>([]); // 입찰 시작 시점의 bid history 스냅샷
  const bidInProgressRef = useRef(false); // React StrictMode 중복 호출 방지
  const animatedButtonRef = useRef<AnimatedBorderButtonRef>(null); // 버튼 ref
  const previousLotEndedRef = useRef<boolean | null>(null); // 이전 lot ended 상태 추적

  // 현재 로트 정보 - LiveBid에서 전달받은 lotIndex에 해당하는 로트 찾기
  const currentLot = useMemo(() => {
    if (!auctionData?.lots || auctionData.lots.length === 0) {
      return null;
    }

    // lotIndex가 있으면 해당 로트를 찾기, disabled가 false인 것 중에서만
    if (lotIndex !== undefined && lotIndex !== null) {
      const enabledLots = auctionData.lots.filter(lot => !lot.disabled);
      const targetLot = enabledLots.find(lot => lot.index === lotIndex);
      if (targetLot) {
        return targetLot;
      }
    }

    // lotIndex가 없거나 해당 로트를 찾을 수 없으면 disabled가 false이면서 ended가 false인 첫 번째 로트
    const enabledLots = auctionData.lots.filter(lot => !lot.disabled);
    const availableLots = enabledLots.filter(lot => !lot.ended);
    
    return availableLots.length > 0 
      ? availableLots.sort((a, b) => a.lotNo - b.lotNo)[0]
      : enabledLots[0] || null;
  }, [auctionData?.lots, lotIndex]);

  devLog('🎯 [LiveBidRequest] 현재 로트:', {
    auctionData: auctionData ? 'available' : 'null',
    currentLot: currentLot ? 'available' : 'null',
    lotCount: auctionData?.lots?.length || 0,
    targetLotIndex: lotIndex,
    selectedLotIndex: currentLot?.index,
    selectedLotNo: currentLot?.lotNo,
    isDisabled: currentLot?.disabled,
    isEnded: currentLot?.ended
  });

  // 입찰 성공 감지 - 새로운 입찰이 들어올 때마다 확인
  useEffect(() => {
    if (bidHistory.length > 0 && isSubmittingBid && bidSubmitTime) {
      const latestBid = bidHistory[0]; // 가장 최근 입찰
      const targetLotIndex = currentLot?.index || lotIndex;
      
      // 입찰 요청 이후에 들어온 입찰인지 확인 (10초 이내로 증가)
      const bidTime = Date.now();
      const isRecentBid = bidTime - bidSubmitTime < 10000; // 10초 이내
      
      // 입찰한 로트가 현재 로트와 일치하는지 확인
      const isTargetLot = latestBid.lot === targetLotIndex;
      
      if (isTargetLot && isRecentBid) {
        devLog('💰 [LiveBidRequest] 새로운 입찰 감지:', {
          latestBid,
          targetLotIndex,
          currentLotIndex: currentLot?.index,
          paramLotIndex: lotIndex,
          bidSubmitTime,
          bidTime,
          isRecentBid,
          isTargetLot
        });
        
        // 입찰 진행 상태 해제
        bidInProgressRef.current = false; // useRef도 초기화
        setIsSubmittingBid(false);
        setBidSubmitTime(null);
        setLastBidRequestId(null); // 성공 시 요청 ID 초기화
        
        // 성공 메시지 표시
        toast.success(`입찰이 성공적으로 접수되었습니다! (${latestBid.code || 'N/A'})`);
        
        // 성공 후 이전 페이지로 돌아가기
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      }
    }
  }, [bidHistory, isSubmittingBid, bidSubmitTime, currentLot?.index, lotIndex, navigate]);

  // 입찰 진행 중 타임아웃 처리
  useEffect(() => {
    if (isSubmittingBid) {
      const timeoutId = setTimeout(() => {
        bidInProgressRef.current = false; // useRef도 초기화
        setIsSubmittingBid(false);
        setBidSubmitTime(null);
        setLastBidRequestId(null); // 타임아웃 시 요청 ID 초기화
        const timeoutMessage = lang === 'ko' 
          ? '입찰 요청 시간이 초과되었습니다. 서버 응답을 받지 못했습니다. 다시 시도해주세요.'
          : 'Bid request timed out. Server response not received. Please try again.';
        toast.error(timeoutMessage);
        devWarn('💰 [LiveBidRequest] 입찰 타임아웃 - 서버 응답 없음');
      }, 15000); // 15초 타임아웃으로 증가

      return () => clearTimeout(timeoutId);
    }
  }, [isSubmittingBid, lang]);

  // 입찰 진행 중 bid history 갱신 감지
  useEffect(() => {
    devLog('🎯 [LiveBidRequest] bid history 감지 useEffect 실행:', {
      bidHistoryLength: bidHistory.length,
      snapshotLength: bidHistorySnapshot.length,
      targetLotIndex: currentLot?.index || lotIndex
    });

    // 스냅샷이 없으면 무시 (버튼을 아직 누르지 않았음)
    if (bidHistorySnapshot.length === 0) {
      devLog('🎯 [LiveBidRequest] bid history 감지 스킵 - 스냅샷 없음');
      return;
    }

    const targetLotIndex = currentLot?.index || lotIndex;
    if (!targetLotIndex) {
      devLog('🎯 [LiveBidRequest] targetLotIndex 없음');
      return;
    }

    // 현재 로트에 대한 새로운 입찰이 추가되었는지 확인
    const currentLotBids = bidHistory.filter(bid => bid.lot === targetLotIndex);
    const snapshotLotBids = bidHistorySnapshot.filter(bid => bid.lot === targetLotIndex);

    // _id 기반으로 새로 추가된 입찰 식별
    const newBids = currentLotBids.filter(currentBid => 
      !snapshotLotBids.some(snapshotBid => snapshotBid._id === currentBid._id)
    );

    // 본인이 아닌 다른 사람의 입찰만 필터링
    const otherUserBids = newBids.filter(bid => 
      bid.user?.index !== user?.index
    );

    devLog('🎯 [LiveBidRequest] bid history 비교:', {
      targetLotIndex,
      현재입찰수: currentLotBids.length,
      스냅샷입찰수: snapshotLotBids.length,
      새입찰수: newBids.length,
      타인입찰수: otherUserBids.length,
      현재사용자Index: user?.index,
      현재입찰들: currentLotBids.map(bid => ({ price: bid.price, user: bid.user?.name || 'unknown', userId: bid.user?.index })),
      스냅샷입찰들: snapshotLotBids.map(bid => ({ price: bid.price, user: bid.user?.name || 'unknown', userId: bid.user?.index })),
      새입찰들: newBids.map(bid => ({ price: bid.price, user: bid.user?.name || 'unknown', userId: bid.user?.index })),
      타인입찰들: otherUserBids.map(bid => ({ price: bid.price, user: bid.user?.name || 'unknown', userId: bid.user?.index }))
    });

    // 다른 사람의 입찰이 있을 때만 모달 표시
    if (otherUserBids.length > 0) {
      devLog('🎯 [LiveBidRequest] 다른 사람의 입찰 감지 - 모달 표시 및 버튼 취소:', {
        targetLotIndex,
        타인입찰수: otherUserBids.length,
        타인입찰들: otherUserBids.map(bid => ({ price: bid.price, user: bid.user?.name || 'unknown', userId: bid.user?.index })),
        animatedButtonRef존재: !!animatedButtonRef.current
      });

      // 버튼의 progress 취소
      if (animatedButtonRef.current) {
        devLog('🎯 [LiveBidRequest] 버튼 progress 취소 호출');
        animatedButtonRef.current.cancelProgress();
      } else {
        devWarn('🎯 [LiveBidRequest] animatedButtonRef.current가 null입니다');
      }

      // 입찰 진행 상태 초기화
      devLog('🎯 [LiveBidRequest] 입찰 진행 상태 초기화');
      bidInProgressRef.current = false;
      setIsSubmittingBid(false);
      setBidSubmitTime(null);
      setLastBidRequestId(null);
      setBidHistorySnapshot([]);

      // 모달 표시
      devLog('🎯 [LiveBidRequest] 모달 표시');
      setShowBidUpdateModal(true);
    }
  }, [bidHistory, bidHistorySnapshot, currentLot?.index, lotIndex, user]);

  // 모달 상태 변화 추적 (디버깅용)
  useEffect(() => {
    devLog('🎯 [LiveBidRequest] 모달 상태 변화:', { showBidUpdateModal });
  }, [showBidUpdateModal]);

  // 입찰 버튼 상태 변화 추적 (디버깅용)
  useEffect(() => {
    devLog('🎯 [LiveBidRequest] 입찰 버튼 상태 변화:', { 
      isSubmittingBid, 
      bidInProgressRef: bidInProgressRef.current,
      hasAnimatedButtonRef: !!animatedButtonRef.current
    });
  }, [isSubmittingBid]);

  // lot 종료 상태 감지
  useEffect(() => {
    if (currentLot) {
      const currentEnded = currentLot.ended;
      const previousEnded = previousLotEndedRef.current;
      
      devLog('🎯 [LiveBidRequest] lot ended 상태 체크:', {
        lotIndex: currentLot.index,
        lotNo: currentLot.lotNo,
        currentEnded,
        previousEnded,
        isFirstCheck: previousEnded === null
      });
      
      // 이전 상태가 false이고 현재 상태가 true인 경우 (false → true로 변경)
      if (previousEnded === false && currentEnded === true) {
        devLog('🎯 [LiveBidRequest] lot 종료 감지 - 모달 표시');
        setShowLotEndedModal(true);
        
        // 입찰 진행 중이었다면 취소
        if (bidHistorySnapshot.length > 0) {
          devLog('🎯 [LiveBidRequest] lot 종료로 인한 입찰 취소');
          if (animatedButtonRef.current) {
            animatedButtonRef.current.cancelProgress();
          }
          bidInProgressRef.current = false;
          setIsSubmittingBid(false);
          setBidSubmitTime(null);
          setLastBidRequestId(null);
          setBidHistorySnapshot([]);
        }
      }
      
      // 현재 상태를 이전 상태로 저장
      previousLotEndedRef.current = currentEnded;
    }
  }, [currentLot, bidHistorySnapshot.length]);

  // 통화 레이블 함수
  const getCurrencyLabel = () => {
    const isDomestic = user?.locationType === 'DOMESTIC';
    return isDomestic ? 'KRW' : 'USD';
  };

  // 동적 입찰가 옵션 생성 함수 - bidHistory 기반으로 수정
  const generateBidPriceOptions = useCallback(() => {
    devLog('🎯 [LiveBidRequest] generateBidPriceOptions 시작:', {
      auctionData: auctionData ? 'available' : 'null',
      biddingRanges: auctionData?.biddingRanges?.length || 0,
      currentLot: currentLot ? 'available' : 'null',
      exchangeRate: currentLot?.exchangeRate,
      bidHistoryLength: bidHistory?.length || 0
    });

    if (!auctionData?.biddingRanges || !currentLot?.exchangeRate || !currentLot) {
      devLog('🎯 [LiveBidRequest] 필수 데이터 부족, 빈 배열 반환');
      return [];
    }

    // 숫자 검증 함수
    const safeNumber = (value: any, defaultValue: number = 0): number => {
      const num = Number(value);
      return isNaN(num) || !isFinite(num) ? defaultValue : num;
    };

    const exchangeRate = safeNumber(currentLot.exchangeRate, 1409.09);
    const startPrice = safeNumber(currentLot.startPrice, 0);
    
    let maxBidPrice = startPrice; // 기본값은 시작가
    
    // bidHistory에서 현재 로트에 해당하는 가장 높은 입찰가 찾기
    if (bidHistory && bidHistory.length > 0) {
      const targetLotIndex = currentLot.index || lotIndex;
      
      devLog('🎯 [LiveBidRequest] bidHistory 분석 시작:', {
        targetLotIndex,
        currentLotIndex: currentLot.index,
        paramLotIndex: lotIndex,
        bidHistoryLength: bidHistory.length,
        allBids: bidHistory.map(bid => ({
          lot: bid.lot,
          price: bid.price,
          user: bid.user?.name || bid.user?.code || 'unknown'
        }))
      });
      
      // 현재 로트에 해당하는 입찰들만 필터링
      const currentLotBids = bidHistory.filter(bid => {
        devLog('🎯 [LiveBidRequest] 입찰 필터링:', {
          bidLot: bid.lot,
          targetLotIndex,
          match: bid.lot === targetLotIndex,
          price: bid.price
        });
        return bid.lot === targetLotIndex;
      });
      
      devLog('🎯 [LiveBidRequest] 필터링된 입찰들:', {
        currentLotBidsCount: currentLotBids.length,
        currentLotBids: currentLotBids.map(bid => ({
          lot: bid.lot,
          price: bid.price,
          user: bid.user?.name || bid.user?.code || 'unknown'
        }))
      });
      
      if (currentLotBids.length > 0) {
        // 가장 높은 입찰가 찾기
        const highestBid = currentLotBids.reduce((max, bid) => {
          const bidPrice = safeNumber(bid.price, 0);
          devLog('🎯 [LiveBidRequest] 입찰가 비교:', {
            currentMax: max,
            bidPrice,
            newMax: bidPrice > max ? bidPrice : max
          });
          return bidPrice > max ? bidPrice : max;
        }, 0);
        
        if (highestBid > 0) {
          maxBidPrice = highestBid;
          devLog('🎯 [LiveBidRequest] bidHistory에서 최고 입찰가 사용:', {
            targetLotIndex,
            currentLotBidsCount: currentLotBids.length,
            highestBid: maxBidPrice,
            startPrice,
            '최고가가_시작가보다_높은가': maxBidPrice > startPrice
          });
        }
      } else {
        devLog('🎯 [LiveBidRequest] 현재 로트에 해당하는 입찰이 없어 시작가 사용:', {
          targetLotIndex,
          startPrice
        });
      }
    }
    
    devLog('🎯 [LiveBidRequest] 가격 정보:', {
      startPrice,
      exchangeRate,
      maxBidPrice,
      bidHistoryUsed: maxBidPrice > startPrice
    });

    // maxBidPrice가 유효하지 않으면 기본값 사용
    if (!isFinite(maxBidPrice) || maxBidPrice <= 0) {
      maxBidPrice = 100000; // 기본 시작가 10만원
      devLog('🎯 [LiveBidRequest] 기본 maxBidPrice 사용:', maxBidPrice);
    }

    // 해당 금액이 속하는 구간 찾기
    const biddingRanges = auctionData.biddingRanges;
    let currentRange = biddingRanges.find((range: BiddingRange) => {
      const minAmount = safeNumber(range.startPrice, 0);
      const maxAmount = range.endPrice !== null ? safeNumber(range.endPrice, 0) : null;
      
      return maxBidPrice >= minAmount && (maxAmount === null || maxBidPrice < maxAmount);
    });

    // 구간을 찾지 못했거나 maxBidPrice가 구간의 최대값과 같은 경우, 다음 구간을 찾기
    if (!currentRange) {
      // maxBidPrice가 어떤 구간의 endPrice와 정확히 일치하는 경우 다음 구간 찾기
      const rangeIndex = biddingRanges.findIndex((range: BiddingRange) => 
        range.endPrice !== null && maxBidPrice === safeNumber(range.endPrice, 0)
      );
      
      if (rangeIndex !== -1 && rangeIndex + 1 < biddingRanges.length) {
        // 다음 구간이 존재하면 사용
        currentRange = biddingRanges[rangeIndex + 1];
      } else {
        // 마지막 구간이거나 구간을 찾을 수 없는 경우
        currentRange = biddingRanges[biddingRanges.length - 1];
      }
    }

    if (!currentRange) {
      // 방어적 코딩: 가장 마지막 구간을 사용
      currentRange = biddingRanges[biddingRanges.length - 1];
      
      if (!currentRange) {
        // 구간이 아예 없는 경우 기본 옵션 생성
        const defaultPriceKrw = maxBidPrice + 1000;
        const defaultPriceUsd = Math.round((defaultPriceKrw / exchangeRate) * 100) / 100;
        
        devLog('🎯 [LiveBidRequest] 기본 옵션 생성:', { defaultPriceKrw, defaultPriceUsd });
        
        return [
          { 
            value: 'default-option-0',
            price: defaultPriceKrw,
            content: (
              <DropdownOptionContainer>
                <CurrencyText>{getCurrencyLabel()}</CurrencyText>
                <AmountText>{user?.locationType === 'DOMESTIC' ? defaultPriceKrw.toLocaleString() : `${defaultPriceUsd.toFixed(2)}`}</AmountText>
              </DropdownOptionContainer>
            )
          }
        ];
      }
    }

    // 시작 금액: 최대 금액 + increment
    const increment = safeNumber(currentRange.increment, 1000);
    const startAmount = maxBidPrice + increment;

    devLog('🎯 [LiveBidRequest] 옵션 생성 파라미터:', {
      increment,
      startAmount,
      exchangeRate
    });

    // 최대 10개까지 옵션 생성
    const options = [];
    for (let i = 0; i < 10; i++) {
      const priceKrw = startAmount + (increment * i);
      const priceUsd = Math.round((priceKrw / exchangeRate) * 100) / 100;
      
      // NaN 검증
      if (!isFinite(priceKrw) || !isFinite(priceUsd) || priceKrw <= 0) {
        devWarn('🎯 [LiveBidRequest] 유효하지 않은 가격 건너뜀:', { priceKrw, priceUsd, i });
        continue;
      }
      
      // 고유한 key를 위해 인덱스와 가격을 조합
      const uniqueKey = `bid-option-${i}-${priceKrw}`;
      
      options.push({
        value: uniqueKey, // 고유한 키 사용
        price: priceKrw, // 실제 가격은 별도 필드로 저장
        content: (
          <DropdownOptionContainer>
            <CurrencyText>{getCurrencyLabel()}</CurrencyText>
            <AmountText>
              {user?.locationType === 'DOMESTIC' 
                ? priceKrw.toLocaleString() 
                : `${priceUsd.toFixed(2)}`
              }
            </AmountText>
          </DropdownOptionContainer>
        )
      });
    }

    devLog('🎯 [LiveBidRequest] 생성된 옵션 수:', options.length);
    return options;
  }, [auctionData, currentLot, user?.locationType, bidHistory, lotIndex]);

  // 입찰가 옵션들
  const bidPriceOptions = useMemo(() => {
    return generateBidPriceOptions();
  }, [generateBidPriceOptions]);

  // 입찰가 옵션이 생성되면 첫 번째 옵션을 기본값으로 설정
  useEffect(() => {
    if (bidPriceOptions.length > 0) {
      devLog('🎯 [LiveBidRequest] bidPriceOptions 변경됨:', {
        optionsCount: bidPriceOptions.length,
        currentSelectedBidPrice: selectedBidPrice,
        firstOption: bidPriceOptions[0]
      });
      
      // 선택된 값이 없거나 빈 문자열이면 첫 번째 옵션으로 설정
      if (!selectedBidPrice || selectedBidPrice === '') {
        devLog('🎯 [LiveBidRequest] 첫 번째 옵션을 기본값으로 설정:', bidPriceOptions[0]);
        setSelectedBidPrice(bidPriceOptions[0].value);
      }
      // 기존 선택된 값이 현재 옵션에 없으면 첫 번째 옵션으로 리셋
      else if (!bidPriceOptions.find(option => option.value === selectedBidPrice)) {
        devLog('🎯 [LiveBidRequest] 기존 선택값이 옵션에 없어 첫 번째 옵션으로 리셋:', bidPriceOptions[0]);
        setSelectedBidPrice(bidPriceOptions[0].value);
      }
    }
  }, [bidPriceOptions, selectedBidPrice]);

  // 툴팁 외부 클릭 시 닫기 및 위치 계산
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showVATTooltip) {
        setShowVATTooltip(false);
      }
    };

    // 툴팁이 표시될 때 위치 계산
    if (showVATTooltip) {
      calculateTooltipPosition();
      // 화면 크기 변경 시에도 위치 재계산
      window.addEventListener('resize', calculateTooltipPosition);
    }

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('resize', calculateTooltipPosition);
    };
  }, [showVATTooltip, calculateTooltipPosition]);

  // 소켓 데이터에서 수수료율 가져오기
  const feeRates = {
    auctionHouseFeeRate: auctionData?.auctionFeeRate || 20, // API에서 가져온 수수료율
    onlineAuctionFeeRate: auctionData?.platformFeeRate || 5, // API에서 가져온 플랫폼 수수료율
    vatRate: 10 // 10% (고정)
  };

  // 수수료 계산 함수 (사용자 locationType에 따른 처리)
  const calculateFees = (bidPrice: number) => {
    const isDomestic = user?.locationType === 'DOMESTIC';
    const exchangeRate = currentLot?.exchangeRate || 1409.09;
    
    // KRW 기준 계산
    const auctionHouseFeeKRW = Math.floor(bidPrice * (feeRates.auctionHouseFeeRate / 100));
    const onlineAuctionFeeKRW = Math.floor(bidPrice * (feeRates.onlineAuctionFeeRate / 100));
    const totalFeesKRW = auctionHouseFeeKRW + onlineAuctionFeeKRW;
    const feeVATKRW = isDomestic ? Math.floor(totalFeesKRW * (feeRates.vatRate / 100)) : 0;
    const totalAmountKRW = bidPrice + totalFeesKRW + feeVATKRW;

    // USD 기준 계산 (해외 사용자용)
    const bidPriceUSD = bidPrice / exchangeRate;
    const auctionHouseFeeUSD = Math.round(bidPriceUSD * (feeRates.auctionHouseFeeRate / 100) * 100) / 100;
    const onlineAuctionFeeUSD = Math.round(bidPriceUSD * (feeRates.onlineAuctionFeeRate / 100) * 100) / 100;
    const totalFeesUSD = auctionHouseFeeUSD + onlineAuctionFeeUSD;
    const feeVATUSD = isDomestic ? Math.round(totalFeesUSD * (feeRates.vatRate / 100) * 100) / 100 : 0;
    const totalAmountUSD = Math.round((bidPriceUSD + totalFeesUSD + feeVATUSD) * 100) / 100;

    return {
      auctionHouseFee: isDomestic ? auctionHouseFeeKRW : auctionHouseFeeUSD,
      onlineAuctionFee: isDomestic ? onlineAuctionFeeKRW : onlineAuctionFeeUSD,
      feeVAT: isDomestic ? feeVATKRW : feeVATUSD,
      totalAmount: isDomestic ? totalAmountKRW : totalAmountUSD,
      isDomestic
    };
  };

  const formatNumber = (value: number) => {
    const isDomestic = user?.locationType === 'DOMESTIC';
    if (isDomestic) {
      return value.toLocaleString();
    } else {
      // 해외 사용자는 USD로 표시
      return value.toFixed(2);
    }
  };

  // 이미지 URL 처리 함수
  const getImageSrc = (imageUrl?: string | null) => {
    if (!imageUrl || imageUrl.trim() === "") {
      return "/common/no_image.svg";
    }
    
    // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
    if (imageUrl.startsWith("http") || imageUrl.startsWith("blob:")) {
      return imageUrl;
    }
    
    // 절대 경로인 경우 (/로 시작)
    if (imageUrl.startsWith("/")) {
      return `${process.env.REACT_APP_API_HOST}${imageUrl}`;
    }
    
    // 단순 파일명인 경우 (서버에서 받은 파일명)
    return `${process.env.REACT_APP_API_HOST}/${imageUrl}`;
  };

  // 기본 auctioneer 약관 (API에서 가져올 수 없을 때만 사용)
  const getDefaultAuctioneerTerms = useCallback(() => {
    const auctioneerName = auctionData?.auctioneer?.name || '경매장';
    
    return `
      <h3>${auctioneerName} 이용약관</h3>
      <p>약관 정보를 불러올 수 없습니다. 잠시 후 다시 시도해주세요.</p>
    `;
  }, [auctionData]);

  // 약관 섹션 데이터 - 소켓 데이터에서 가져오거나 기본값 사용
  const termsSection = {
    key: 'auction-terms',
    title: texts.auctionTermsDetailTitle,
    content: auctionData?.auctioneer?.term || getDefaultAuctioneerTerms()
  };

  const handleViewTerms = () => {
    setShowTermsSheet(true);
  };

  const handleCloseTermsSheet = () => {
    setShowTermsSheet(false);
  };

  const handleSectionClick = (sectionKey: string) => {
    setViewedSection(sectionKey);
  };

  const isFormValid = () => {
    return selectedBidPrice && isIndividualAgreed && !isSubmittingBid;
  };

  const handleBidSubmit = () => {
    // 차단된 사용자인지 확인
    if (isBanned) {
      setShowBidRestrictedModal(true);
      return;
    }

    // React StrictMode나 빠른 중복 클릭 방지
    if (bidInProgressRef.current) {
      devWarn('💰 [LiveBidRequest] 입찰이 이미 진행 중입니다 (useRef 차단)');
      const alreadyInProgressMessage = lang === 'ko' ? '입찰이 이미 진행 중입니다.' : 'Bid is already in progress.';
      toast.warning(alreadyInProgressMessage);
      return;
    }

    // 실제 입찰할 로트 인덱스 결정 - currentLot 우선, 없으면 파라미터 lotIndex
    const targetLotIndex = currentLot?.index || lotIndex;
    
    if (!targetLotIndex || !selectedBidPrice || !auctionIndex) {
      const invalidBidInfoMessage = lang === 'ko' ? '입찰 정보가 올바르지 않습니다.' : 'Bid information is invalid.';
      toast.error(invalidBidInfoMessage);
      devError('💰 [LiveBidRequest] 입찰 정보 부족:', {
        targetLotIndex,
        selectedBidPrice,
        auctionIndex,
        currentLot: currentLot?.index,
        paramLotIndex: lotIndex
      });
      return;
    }

    // 소켓 연결 상태 확인
    if (!isConnected) {
      const socketDisconnectedMessage = lang === 'ko' 
        ? '소켓 연결이 끊어져 있습니다. 잠시 후 다시 시도해주세요.'
        : 'Socket connection is disconnected. Please try again later.';
      toast.error(socketDisconnectedMessage);
      return;
    }

    // 이미 입찰 진행 중인 경우 중복 방지
    if (isSubmittingBid) {
      const bidInProgressMessage = lang === 'ko' 
        ? '입찰이 진행 중입니다. 잠시만 기다려주세요.'
        : 'Bid is in progress. Please wait a moment.';
      toast.warning(bidInProgressMessage);
      return;
    }

    // 선택된 옵션에서 실제 가격 찾기
    const selectedOption = bidPriceOptions.find(option => option.value === selectedBidPrice);
    const actualPrice = selectedOption?.price;

    if (!actualPrice) {
      const invalidPriceMessage = lang === 'ko' ? '입찰가가 올바르지 않습니다.' : 'Bid price is invalid.';
      toast.error(invalidPriceMessage);
      return;
    }

    // 중복 방지: 같은 입찰 요청 ID 생성 및 확인
    const bidRequestId = `${auctionIndex}-${targetLotIndex}-${actualPrice}-${Date.now()}`;
    
    if (lastBidRequestId === bidRequestId) {
      devWarn('💰 [LiveBidRequest] 중복 입찰 요청 차단:', bidRequestId);
      const duplicateRequestMessage = lang === 'ko' 
        ? '동일한 입찰 요청이 이미 진행 중입니다.'
        : 'The same bid request is already in progress.';
      toast.warning(duplicateRequestMessage);
      return;
    }

    devLog('💰 [LiveBidRequest] 입찰 완료 - API 호출 실행:', {
      auctionIndex,
      lotIndex: targetLotIndex,
      price: actualPrice,
      bidRequestId,
      bidHistorySnapshotLength: bidHistorySnapshot.length
    });
    
    try {
      // 입찰 진행 상태 설정 및 요청 ID 저장
      bidInProgressRef.current = true; // useRef로 즉시 차단
      setIsSubmittingBid(true);
      setBidSubmitTime(Date.now()); // 입찰 요청 시간 기록
      setLastBidRequestId(bidRequestId); // 중복 방지용 ID 저장
      
      // 바로 입찰 요청 (이미 경매방에 참여된 상태)
      devLog('💰 [LiveBidRequest] 입찰 요청 전송');
      placeBid(auctionIndex, targetLotIndex, actualPrice);
      
      // 진행 상태 메시지
      const bidProgressMessage = lang === 'ko' ? '입찰을 진행하고 있습니다...' : 'Bidding in progress...';
      toast.info(bidProgressMessage);
      
    } catch (error) {
      devError('💰 [LiveBidRequest] 입찰 신청 실패:', error);
      bidInProgressRef.current = false; // 실패 시 즉시 해제
      setIsSubmittingBid(false);
      setBidSubmitTime(null);
      setLastBidRequestId(null); // 실패 시 요청 ID 초기화
      setBidHistorySnapshot([]); // 스냅샷 초기화
      const bidErrorMessage = lang === 'ko' ? '입찰 신청 중 오류가 발생했습니다.' : 'An error occurred during bid submission.';
      toast.error(bidErrorMessage);
    }
  };

  const handleIncomplete = () => {
    // 완료되지 않았을 때의 처리
    devLog('🎯 [LiveBidRequest] 입찰 신청이 완료되지 않았습니다 (버튼에서 호출됨)');
  };

  const handleDisabled = () => {
    // 비활성화 상태일 때의 처리
    const completeInfoMessage = lang === 'ko' ? '모든 정보를 입력하고 동의해주세요' : 'Please enter all information and agree to terms';
    toast.warning(completeInfoMessage);
  };

  // 버튼을 누르기 시작할 때 호출 (스냅샷 저장)
  const handleStartPress = () => {
    devLog('💰 [LiveBidRequest] 버튼 누르기 시작 - 스냅샷 저장:', {
      currentBidHistoryLength: bidHistory.length,
      targetLotIndex: currentLot?.index || lotIndex,
      isSubmittingBid
    });
    
    // 입찰 시작 시점의 bid history 스냅샷만 저장 (상태 변경 없음)
    setBidHistorySnapshot([...bidHistory]);
  };

  // bid history 갱신 모달 핸들러
  const handleBidUpdateModalClose = () => {
    setShowBidUpdateModal(false);
  };

  const handleBidUpdateModalConfirm = () => {
    setShowBidUpdateModal(false);
    // 입찰가 옵션을 다시 생성하여 최신 상태로 업데이트
    // generateBidPriceOptions가 bidHistory에 의존하므로 자동으로 업데이트됨
  };

  // lot 종료 모달 핸들러
  const handleLotEndedModalClose = () => {
    setShowLotEndedModal(false);
    // lot가 종료되었으므로 이전 페이지로 돌아가기
    navigate(-1);
  };

  const Layout = (
    <MainLayout currentPage="home">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="home"
          title={`${texts.title} ${!isConnected ? '(연결 중...)' : ''}`}
          showBackButton={true}
          onBack={() => navigate(-1)}
        />
        <Container>
          <ContentContainer>
            {/* 정보 섹션 */}
            <InfoSection>
              <LotImage 
                src={currentLot?.images?.[0] ? getImageSrc(currentLot.images[0]) : "/common/no_image.svg"} 
                alt="Lot Image"
                onError={(e) => {
                  e.currentTarget.src = '/common/no_image.svg';
                }}
              />
              <LotInfoContainer>
                <LotName>
                  {currentLot?.lotName || (!isConnected ? '데이터 로딩 중...' : '로트 정보를 불러올 수 없습니다.')}
                </LotName>
                <LotId>{currentLot?.lotCode || `LOT.${currentLot?.lotNo || '---'}`}</LotId>
              </LotInfoContainer>
            </InfoSection>

            {/* 금액 섹션 */}
            <div>
              <FormTitle>{texts.amountSection}</FormTitle>
              <AmountSection>
                <DropdownContainer>
                  <CustomDropdown
                    value={selectedBidPrice}
                    onChange={setSelectedBidPrice}
                    options={bidPriceOptions}
                    placeholder={
                      <DropdownOptionContainer>
                        <CurrencyText>{getCurrencyLabel()}</CurrencyText>
                        <AmountText>{texts.bidAmountPlaceholder}</AmountText>
                      </DropdownOptionContainer>
                    }
                  />
                </DropdownContainer>
                {selectedBidPrice && (
                  <FeeCalculationContainer>
                    {(() => {
                      // 선택된 옵션에서 실제 가격 찾기
                      const selectedOption = bidPriceOptions.find(option => option.value === selectedBidPrice);
                      const bidPrice = selectedOption?.price || 0;
                      
                      if (bidPrice <= 0) {
                        return <div>유효하지 않은 입찰가입니다.</div>;
                      }
                      
                      const fees = calculateFees(bidPrice);
                      return (
                        <>
                          <FeeRow>
                            <span>{texts.auctionHouseFee} ({feeRates.auctionHouseFeeRate}%)</span>
                            <span>{getCurrencyLabel()} {formatNumber(fees.auctionHouseFee)}</span>
                          </FeeRow>
                          <FeeRow>
                            <span>{texts.onlineAuctionFee} ({feeRates.onlineAuctionFeeRate}%)</span>
                            <span>{getCurrencyLabel()} {formatNumber(fees.onlineAuctionFee)}</span>
                          </FeeRow>
                          {fees.isDomestic && (
                            <FeeRow>
                              <TooltipContainer>
                                <span>{texts.feeVAT} ({feeRates.vatRate}%)</span>
                                <QuestionIcon 
                                  ref={questionIconRef}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowVATTooltip(!showVATTooltip);
                                  }}
                                >
                                  ?
                                </QuestionIcon>
                                <Tooltip 
                                  visible={showVATTooltip}
                                  $left={tooltipPosition.left}
                                  $transform={tooltipPosition.transform}
                                  $arrowLeft={tooltipPosition.arrowLeft}
                                >
                                  {texts.feeVATTooltip}
                                </Tooltip>
                              </TooltipContainer>
                              <span>{getCurrencyLabel()} {formatNumber(fees.feeVAT)}</span>
                            </FeeRow>
                          )}
                          <FeeDivider />
                          <TotalRow>
                            <span>{texts.totalAmount}</span>
                            <span>{getCurrencyLabel()} {formatNumber(fees.totalAmount)}</span>
                          </TotalRow>
                        </>
                      );
                    })()}
                  </FeeCalculationContainer>
                )}
              </AmountSection>
            </div>

            {/* 동의 섹션 */}
            <div>
              {/* 입찰 시 유의사항 */}
              <BiddingNoticeContainer>
                <BiddingNoticeTitle>
                  <InfoIcon viewBox="0 0 24 24">
                    <path d="M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2ZM13,17H11V11H13V17ZM13,9H11V7H13V9Z" />
                  </InfoIcon>
                  {texts.biddingNoticeTitle}
                </BiddingNoticeTitle>
                <BiddingNoticeList>
                  <BiddingNoticeItem>{texts.biddingNoticeItem1}</BiddingNoticeItem>
                  <BiddingNoticeItem>{texts.biddingNoticeItem2}</BiddingNoticeItem>
                  <BiddingNoticeItem>{texts.biddingNoticeItem3}</BiddingNoticeItem>
                </BiddingNoticeList>
              </BiddingNoticeContainer>
              
              <AgreementSectionWrapper>
                <AgreementItem>
                  <AgreementButton
                    isClicked={isIndividualAgreed}
                    onClicked={() => setIsIndividualAgreed(!isIndividualAgreed)}
                  >
                    {texts.auctionTermsTitle}
                  </AgreementButton>
                  <ViewButton onClick={handleViewTerms}>
                    보기
                  </ViewButton>
                </AgreementItem>
              </AgreementSectionWrapper>
            </div>
          </ContentContainer>
          
          {/* 고정 버튼 영역 */}
          <FixedButtonContainer>
            <ButtonWrapper>
              <AnimatedBorderButton
                ref={animatedButtonRef}
                title={isSubmittingBid ? '입찰 진행 중...' : texts.bidButton}
                tooltipText={isSubmittingBid ? '입찰이 진행 중입니다' : texts.bidButtonTooltip}
                onStartPress={handleStartPress}
                onPressed={handleBidSubmit}
                onIncomplete={handleIncomplete}
                disabledCallback={handleDisabled}
                duration={1}
                height={48}
                borderRadius={16}
                borderThickness={3}
                backgroundColor="#A6D4D1"
                finalBorderColor="#22c55e"
                textColor="#262626"
                textPressedColor="#FFFFFF"
                textCompletedColor="#FFFFFF"
                disabled={!isFormValid()}
                hideTooltip={showTermsSheet} // 바텀시트가 열릴 때 툴팁 숨기기
              />
            </ButtonWrapper>
          </FixedButtonContainer>
        </Container>
        
        {/* 약관 동의서 시트 */}
        {showTermsSheet && (
          <TermsConsentSheet
            sections={[termsSection]}
            viewedSection={viewedSection}
            onClose={handleCloseTermsSheet}
            onSectionClick={handleSectionClick}
            buttonText={texts.agreeButton}
            onButtonClick={() => {
              setIsIndividualAgreed(true);
              handleCloseTermsSheet();
            }}
          />
        )}

        {/* 입찰 갱신 모달 */}
        <BidHistoryUpdateModal
          isOpen={showBidUpdateModal}
          onClose={handleBidUpdateModalClose}
          onConfirm={handleBidUpdateModalConfirm}
        />

        {/* lot 종료 모달 */}
        <LotEndedModal
          isOpen={showLotEndedModal}
          onClose={handleLotEndedModalClose}
        />

        {/* 입찰 제한 모달 */}
        <BidRestrictedModal
          isOpen={showBidRestrictedModal}
          onClose={() => setShowBidRestrictedModal(false)}
        />
      </CenteredLayout>
    </MainLayout>
  );

  return (
    <ResponsiveView
      mobileView={Layout}
      desktopView={Layout}
    />
  );
}

export default LiveBidRequest;
